import { useCallback, useEffect, useRef, useState } from "react";

interface Props {
  /** Inclusive lower bound of the underlying axis (e.g. dataStartMs). */
  min: number;
  /** Inclusive upper bound. */
  max: number;
  /** Currently selected window. */
  value: { start: number; end: number };
  /** Called continuously while the user drags. */
  onChange: (next: { start: number; end: number }) => void;
  /** Called once when the drag ends (good place to persist). */
  onCommit?: (next: { start: number; end: number }) => void;
  /** Optional formatter for the handle hint (Date label). */
  formatTick?: (v: number) => string;
}

type DragMode = "start" | "end" | "window" | null;

/**
 * Two-handle range brush. Hand-rolled so we keep the bundle small and the
 * look consistent with the rest of the dashboard. Works with mouse and
 * touch via the Pointer Events API. The parent owns the value (controlled
 * component); we only emit on change/commit.
 *
 * Below the track we render light "data extents" labels so the user always
 * sees what the full window is, even after zooming in.
 */
export function RangeSlider({ min, max, value, onChange, onCommit, formatTick }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<DragMode>(null);
  // Snapshot the value at drag start so window-drags don't drift.
  const dragStartRef = useRef<{ pointerFrac: number; start: number; end: number } | null>(null);

  const span = Math.max(1, max - min);
  const startFrac = clamp01((value.start - min) / span);
  const endFrac = clamp01((value.end - min) / span);

  const fracFromClientX = useCallback((clientX: number): number => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return clamp01((clientX - rect.left) / rect.width);
  }, []);

  const onPointerDown = (mode: Exclude<DragMode, null>) => (e: React.PointerEvent) => {
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    setDrag(mode);
    dragStartRef.current = {
      pointerFrac: fracFromClientX(e.clientX),
      start: value.start,
      end: value.end,
    };
  };

  // Global pointer-move while dragging.
  useEffect(() => {
    if (!drag) return;
    const move = (e: PointerEvent) => {
      const frac = fracFromClientX(e.clientX);
      const v = min + frac * span;
      const snap = dragStartRef.current;
      if (drag === "start") {
        const nextStart = Math.min(v, value.end - span * 0.005);
        onChange({ start: clamp(nextStart, min, max), end: value.end });
      } else if (drag === "end") {
        const nextEnd = Math.max(v, value.start + span * 0.005);
        onChange({ start: value.start, end: clamp(nextEnd, min, max) });
      } else if (drag === "window" && snap) {
        const dFrac = frac - snap.pointerFrac;
        const width = snap.end - snap.start;
        let s = snap.start + dFrac * span;
        let e2 = s + width;
        if (s < min) {
          s = min;
          e2 = s + width;
        }
        if (e2 > max) {
          e2 = max;
          s = e2 - width;
        }
        onChange({ start: s, end: e2 });
      }
    };
    const up = () => {
      setDrag(null);
      dragStartRef.current = null;
      onCommit?.(value);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, [drag, fracFromClientX, min, max, span, value.start, value.end, value, onChange, onCommit]);

  const startPct = `${(startFrac * 100).toFixed(2)}%`;
  const endPct = `${(endFrac * 100).toFixed(2)}%`;
  const widthPct = `${((endFrac - startFrac) * 100).toFixed(2)}%`;

  return (
    <div className="select-none">
      <div
        ref={trackRef}
        className="relative h-6 mx-1 my-1 touch-none"
        style={{ cursor: drag ? "grabbing" : undefined }}
      >
        {/* Background track */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-slate-200 dark:bg-slate-800" />
        {/* Selected window */}
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-sky-400/70 dark:bg-sky-500/60 cursor-grab active:cursor-grabbing"
          style={{ left: startPct, width: widthPct }}
          onPointerDown={onPointerDown("window")}
        />
        {/* Start handle */}
        <button
          type="button"
          aria-label="Anfang"
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-4 w-4 rounded-full bg-white dark:bg-slate-200 border border-slate-400 dark:border-slate-500 shadow cursor-ew-resize"
          style={{ left: startPct }}
          onPointerDown={onPointerDown("start")}
        />
        {/* End handle */}
        <button
          type="button"
          aria-label="Ende"
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-4 w-4 rounded-full bg-white dark:bg-slate-200 border border-slate-400 dark:border-slate-500 shadow cursor-ew-resize"
          style={{ left: endPct }}
          onPointerDown={onPointerDown("end")}
        />
      </div>
      {formatTick && (
        <div className="flex justify-between text-[10px] text-slate-500 dark:text-slate-400 tabular-nums px-1">
          <span>{formatTick(value.start)}</span>
          <span>{formatTick(value.end)}</span>
        </div>
      )}
    </div>
  );
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
function clamp01(v: number): number {
  return clamp(v, 0, 1);
}
